import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, Scrolling, SearchPanel, Selection, Editing } from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import Form, { SimpleItem } from 'devextreme-react/form';
import notify from 'devextreme/ui/notify';
import competitionEntityApi from '../../../api/competitionEntityApi';
import { SplitPanel } from '../../utils/split-panel/SplitPanel';
import classNames from 'classnames';

const CoachesLeftPane = React.memo(React.forwardRef(function CoachesLeftPane({ dataSource, onSelect, onSaving, isSaving, clubLookup, canEdit }, ref) {
    const gridRef = useRef(null);
    React.useImperativeHandle(ref, () => ({
        clearSelection: () => gridRef.current?.instance?.clearSelection(),
    }), []);
    return (
        <div className='dx-card' style={{ padding: 12, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
            <DataGrid
                ref={gridRef}
                className='grid theme-dependent'
                dataSource={dataSource}
                keyExpr='coachId'
                columnAutoWidth
                rowAlternationEnabled
                hoverStateEnabled
                showBorders={false}
                repaintChangesOnly
                onSelectionChanged={(e) => { const row = e.selectedRowsData?.[0]; onSelect(row ? row.coachId : null); }}
                onSaving={onSaving}
                loadPanel={{ enabled: isSaving, text: 'Speichern...' }}
                style={{ flex: 1, minHeight: 0 }}
            >
                <SearchPanel visible placeholder='Suche nach Name, Verein...' />
                <Selection mode='single' />
                <Column dataField='name' caption='Nachname' width={140} />
                <Column dataField='firstname' caption='Vorname' width={140} />
                <Column dataField='clubId' caption='Verein' lookup={{ dataSource: clubLookup, valueExpr: 'id', displayExpr: 'name' }} />
                <Editing mode='popup' allowAdding={canEdit} useIcons />
                <Scrolling mode='virtual' />
            </DataGrid>
        </div>
    );
}));

export function CompetitionCoaches({ competitionId, data, clubs, accessToken, onEntityChange, dataSource }) {
    const [selectedId, setSelectedId] = useState(null);
    const [formData, setFormData] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const canEdit = !!accessToken;

    const clubLookup = useMemo(() => (
        clubs?.map((club) => ({ id: club.clubId, name: club.shortname || club.name })) || []
    ), [clubs]);

    const storeItems = dataSource?.store?._array || data || [];
    const storeRef = useRef(storeItems);
    useEffect(() => { storeRef.current = dataSource?.store?._array || data || []; });

    const count = storeItems.length;

    useEffect(() => {
        if (!selectedId) { setFormData(null); setIsDirty(false); return; }
        const item = storeRef.current.find(c => c.coachId === selectedId);
        setFormData(item ? { ...item } : null);
        setIsDirty(false);
        setIsEditing(false);
    }, [selectedId]);

    const handleSelect = useCallback((id) => setSelectedId(id), []);
    const leftPaneRef = useRef(null);

    const updateField = useCallback((field, value) => {
        setFormData(prev => prev ? { ...prev, [field]: value } : prev);
        setIsDirty(true);
    }, []);

    const handleSave = useCallback(async () => {
        if (!formData || !accessToken || !selectedId) return;
        setIsSaving(true);
        try {
            const current = storeRef.current.find(c => c.coachId === selectedId) || {};
            const saved = await competitionEntityApi.coaches.update(competitionId, selectedId, { ...current, ...formData, coachId: selectedId, competitionId }, accessToken);
            onEntityChange?.('Coach', 'update', saved);
            setIsDirty(false);
            setIsEditing(false);
            notify('Gespeichert', 'success', 2000);
        } catch (err) {
            notify(err?.response?.data || err?.message || 'Fehler beim Speichern', 'error', 4000);
        } finally {
            setIsSaving(false);
        }
    }, [accessToken, competitionId, formData, onEntityChange, selectedId]);

    const handleCancel = useCallback(() => {
        const item = storeRef.current.find(c => c.coachId === selectedId);
        setFormData(item ? { ...item } : null);
        setIsDirty(false);
        setIsEditing(false);
    }, [selectedId]);

    const handleDelete = useCallback(async () => {
        if (!accessToken || !selectedId) return;
        if (!window.confirm('Trainer wirklich löschen?')) return;
        setIsSaving(true);
        try {
            await competitionEntityApi.coaches.remove(competitionId, selectedId, accessToken);
            onEntityChange?.('Coach', 'delete', { coachId: selectedId });
            setSelectedId(null);
            leftPaneRef.current?.clearSelection();
            notify('Gelöscht', 'success', 2000);
        } catch (err) {
            notify(err?.response?.data || err?.message || 'Fehler beim Löschen', 'error', 4000);
        } finally {
            setIsSaving(false);
        }
    }, [accessToken, competitionId, onEntityChange, selectedId]);

    const handleGridSaving = useCallback(async (e) => {
        if (!e.changes?.length) return;
        if (!accessToken) { notify('Kein Zugriffstoken verfügbar.', 'warning', 3000); e.cancel = true; return; }
        e.cancel = true;
        setIsSaving(true);
        try {
            for (const change of e.changes) {
                if (change.type === 'insert') {
                    const payload = { ...change.data, competitionId };
                    const saved = await competitionEntityApi.coaches.insert(competitionId, payload, accessToken);
                    onEntityChange?.('Coach', 'insert', saved);
                }
            }
            e.component.cancelEditData();
        } catch (err) {
            notify(err?.response?.data || err?.message || 'Fehler beim Speichern', 'error', 4000);
        } finally {
            setIsSaving(false);
        }
    }, [accessToken, competitionId, onEntityChange]);

    const renderFormToolbar = useCallback((title) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
            {canEdit && (
                <div style={{ display: 'flex', gap: 6 }}>
                    {isEditing ? (
                        <>
                            <Button text='Speichern' type='default' stylingMode='contained' disabled={isSaving} onClick={handleSave} />
                            <Button text='Abbrechen' stylingMode='outlined' disabled={isSaving} onClick={handleCancel} />
                            <Button icon='trash' stylingMode='outlined' hint='Löschen' disabled={isSaving} onClick={handleDelete} />
                        </>
                    ) : (
                        <>
                            <Button text='Bearbeiten' icon='edit' stylingMode='outlined' disabled={!formData} onClick={() => setIsEditing(true)} />
                            <Button icon='trash' stylingMode='outlined' hint='Löschen' disabled={!formData || isSaving} onClick={handleDelete} />
                        </>
                    )}
                </div>
            )}
        </div>
    ), [canEdit, formData, handleCancel, handleDelete, handleSave, isEditing, isSaving]);

    const renderDetail = () => {
        if (!formData) {
            return (
                <div className='dx-card' style={{ padding: 20, color: '#888' }}>
                    Bitte einen Trainer aus der Liste links auswählen.
                </div>
            );
        }
        const f = formData;
        return (
            <div className={classNames('dx-card', 'plain-styled-form', { 'view-mode': !isEditing })} style={{ padding: 16 }}>
                {renderFormToolbar(`${f.firstname || ''} ${f.name || ''}`.trim())}
                <Form formData={f} labelMode='floating' colCount={4} readOnly={!isEditing} onFieldDataChanged={(e) => updateField(e.dataField, e.value)}>
                    <SimpleItem dataField='name' label={{ text: 'Nachname' }} editorOptions={{ stylingMode: 'filled' }} />
                    <SimpleItem dataField='firstname' label={{ text: 'Vorname' }} editorOptions={{ stylingMode: 'filled' }} />
                    <SimpleItem dataField='gender' label={{ text: 'Geschlecht' }} editorOptions={{ stylingMode: 'filled' }} />
                    <SimpleItem dataField='clubId' label={{ text: 'Verein' }} editorType='dxSelectBox' editorOptions={{ dataSource: clubLookup, valueExpr: 'id', displayExpr: 'name', stylingMode: 'filled' }} />
                </Form>
            </div>
        );
    };

    return (
        <div className='competition-section'>
            <div className='section-header'>
                <div>
                    <div className='section-title'>Trainer</div>
                    <div className='section-subtitle'>Betreuerpersonal der Vereine</div>
                </div>
                <div className='section-pill subtle'>{count} Trainer</div>
            </div>

            <SplitPanel
                storageKey='splitPanel.coaches'
                defaultLeft={320}
                minLeft={160}
                minRight={200}
                left={<CoachesLeftPane ref={leftPaneRef} dataSource={dataSource} onSelect={handleSelect} onSaving={handleGridSaving} isSaving={isSaving} clubLookup={clubLookup} canEdit={canEdit} />}
                right={renderDetail()}
            />
        </div>
    );
}
